import React from 'react'
import { useSelector } from 'react-redux'
import Link from 'redux-first-router-link'
import { css } from '@emotion/react'
import { selectAccountPreferenceChatEnabled } from 'selectors/app/selectAccountPreferences'
import { selectAdminAgents } from 'selectors/agents/base'
import { selectCurrentUserIsAdmin } from 'ducks/currentUser/selectors'
import {
  selectCurrentUserHasChatAccessToCurrentWidget,
  selectCurrentUserHasChatAccessToSomeWidgets,
  selectWidgetFromUrl,
  selectWidgetsWithChat,
  selectWidgetsIds,
} from 'ducks/widgets/selectors/widgets'
import WithGlobalNav from 'components/App/DesktopView/Layout/GlobalNav/WithGlobalNav'
import AppNotices from 'components/AppNotices'
import {
  heading,
  text,
} from '@groovehq/internal-design-system/lib/styles/elements'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'

import liveChatImage from 'assets/chat/illustrations/livechat@2x.png'
import accessAgentImage from 'assets/chat/illustrations/access-agent@2x.png'
import accessAdminImage from 'assets/chat/illustrations/access-admin@2x.png'

import {
  SETTINGS_WIDGETS_PAGE,
  SETTINGS_WIDGET_CREATE_NEW_PAGE,
  SETTINGS_WIDGET_CHAT_ACL_PAGE,
} from 'subapps/settings/types'
import { SUI } from 'shared/ui'

const containerStyle = () => theme => css`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-left: 6px solid ${theme.color.mainNav.background};
`

const contentStyle = css`
  max-width: 720px;
  margin: 30px 30px 100px 30px;
  text-align: center;
`

const paragraphStyle = [
  text.styles.textNormal,
  text.styles.textDark,
  // HACK (jscheel): Internal design system doesn't color links yet
  css`
    & a {
      color: var(--color-primary-brand);
    }
  `,
]

const AdminList = () => {
  const admins = useSelector(selectAdminAgents)
  return (
    <p css={paragraphStyle} className="grui mt-15">
      {admins.map(admin => (
        <SUI>
          <strong>{admin.name}</strong>
          {' - '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`mailto:${admin.email}?subject=Chat%20access%20on%20Groove`}
          >
            {admin.email}
          </a>
          <br />
        </SUI>
      ))}
    </p>
  )
}

const ErrorPage = () => {
  const isChatEnabled = useSelector(selectAccountPreferenceChatEnabled)
  const hasChatAccessToCurrentWidget = useSelector(
    selectCurrentUserHasChatAccessToCurrentWidget
  )
  const hasChatAccessToSomeWidgets = useSelector(
    selectCurrentUserHasChatAccessToSomeWidgets
  )
  const isAdmin = useSelector(selectCurrentUserIsAdmin)
  const currentWidget = useSelector(selectWidgetFromUrl)
  const hasWidgets = useSelector(selectWidgetsIds).length > 0
  const hasWidgetsWithChatEnabled =
    useSelector(selectWidgetsWithChat).length > 0

  let content = (
    <React.Fragment>
      <img src={liveChatImage} width="216" height="179" aria-hidden alt="" />
      <h1 css={heading.styles.h2}>Live chat early access is here!</h1>
      <p css={[text.styles.textNormal, text.styles.textDark]}>
        Stay in touch with your customers on your website or app.<br />All
        features, no limits during the early access trial.
      </p>
      <Link
        to={{
          type: SETTINGS_WIDGET_CREATE_NEW_PAGE,
        }}
      >
        <Button className="grui mt-8">Set up your live chat widget</Button>
      </Link>
    </React.Fragment>
  )

  if (!isChatEnabled && isAdmin) {
    content = (
      <React.Fragment>
        <img src={liveChatImage} width="216" height="179" aria-hidden alt="" />
        <h1 css={heading.styles.h2}>Live chat early access is here!</h1>
        <p css={paragraphStyle}>
          Stay in touch with your customers on your website or app.<br />All
          features, no limits during the early access trial.
        </p>
        <Link
          to={{
            type: SETTINGS_WIDGET_CREATE_NEW_PAGE,
          }}
        >
          <Button className="grui mt-8">Set up your live chat widget</Button>
        </Link>
      </React.Fragment>
    )
  } else if (isAdmin && !hasWidgets) {
    content = (
      <React.Fragment>
        <img src={liveChatImage} width="216" height="179" aria-hidden alt="" />
        <h1 css={heading.styles.h2}>Live chat early access is here!</h1>
        <p css={paragraphStyle}>
          Stay in touch with your customers on your website or app.<br />All
          features, no limits during the early access trial.
        </p>
        <Link
          to={{
            type: SETTINGS_WIDGET_CREATE_NEW_PAGE,
          }}
        >
          <Button className="grui mt-8">Set up your live chat widget</Button>
        </Link>
      </React.Fragment>
    )
  } else if (isAdmin && hasWidgetsWithChatEnabled) {
    content = (
      <React.Fragment>
        <img src={liveChatImage} width="216" height="179" aria-hidden alt="" />
        <h1 css={heading.styles.h2}>Live chat early access is here!</h1>
        <p css={paragraphStyle}>
          Stay in touch with your customers on your website or app through your
          Groove widget.<br />All features included with no limits during the
          early access trial.
        </p>
        <p css={paragraphStyle}>
          If you need a hand,{' '}
          <a
            href="https://help.groovehq.com/help/adding-live-chat-to-an-existing-widget"
            rel="noopener noreferrer"
            target="_blank"
          >
            here’s a helpful guide
          </a>.
        </p>
        <Link
          to={{
            type: SETTINGS_WIDGETS_PAGE,
          }}
        >
          <Button className="grui mt-8">Add live chat to your widgets</Button>
        </Link>
      </React.Fragment>
    )
  } else if (!isAdmin && !hasChatAccessToSomeWidgets) {
    content = (
      <React.Fragment>
        <img
          src={accessAgentImage}
          width="203"
          height="178"
          aria-hidden
          alt=""
        />
        <h1 css={heading.styles.h2}>Oh no! You have no access to Live chat</h1>
        <p css={paragraphStyle}>
          Once you are given access to Live chat, you will be able to access all
          messages here. Please chat to one of your account admins to give you
          access:
        </p>
        <AdminList />
      </React.Fragment>
    )
  } else if (isChatEnabled && !isAdmin && !hasChatAccessToCurrentWidget) {
    content = (
      <React.Fragment>
        <img
          src={accessAgentImage}
          width="203"
          height="178"
          aria-hidden
          alt=""
        />
        <h1 css={heading.styles.h2}>
          You don&#39;t have access to this Live chat
        </h1>
        <p css={paragraphStyle}>
          You don&#39;t have access to this Live chat channel. Once you are
          given access to Live chat, you will be able to access its messages
          here. Please chat to one of your account admins to give you access:
        </p>
        <AdminList />
      </React.Fragment>
    )
  } else if (isChatEnabled && isAdmin && !hasChatAccessToCurrentWidget) {
    content = (
      <React.Fragment>
        <img
          src={accessAdminImage}
          width="194"
          height="164"
          aria-hidden
          alt=""
        />
        <h1 css={heading.styles.h2}>
          You don&#39;t have access to this Live chat
        </h1>
        <p css={paragraphStyle}>
          You don&#39;t have access to this Live chat channel. You can give
          yourself access permission by adding yourself to the team members
          here.
        </p>
        <Link
          to={{
            type: SETTINGS_WIDGET_CHAT_ACL_PAGE,
            payload: {
              id: currentWidget.id,
            },
          }}
        >
          <Button className="grui mt-8">Update access permissions</Button>
        </Link>
      </React.Fragment>
    )
  }

  return (
    <WithGlobalNav active="Live Chat" topContent={<AppNotices />}>
      <div css={containerStyle}>
        <div css={contentStyle}>{content}</div>
      </div>
    </WithGlobalNav>
  )
}

export default ErrorPage
